import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import PublicSecureLoading from "./PublicSecureLoading";
import jwt from "jsonwebtoken";
import { makeStyles, Container } from "@material-ui/core";
import publicMainStyle from "assets/jss/components/publicMainStyles";
import cx from "classnames";
import PublicHeader from "./PublicHeader";
import PublicFooter from "./PublicFooter";
import { useHistory } from "react-router-dom";
import FooterMain from "components/layout/FooterMain";
import { PrivacyStatement } from "components/login/PrivacyStatement";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import { PUBLIC_USER_DATA_QUERY } from "features/public/queries";
import { emptyString } from "constants/defaultValues";
import { useStateLens } from "@totalsoft/react-state-lens";
import { get, set } from "@totalsoft/react-state-lens";
import { setAccessToken } from "apollo/client";

const useStyles = makeStyles(publicMainStyle);

const PublicComponent = ({ Component }) => {
  const { search } = useLocation();
  const token = new URLSearchParams(search).get("token");
  const [loading, setLoading] = useState(true);
  const stateLens = useStateLens(null);
  const classes = useStyles();
  const history = useHistory();
  const isPublic = true;
  const isGdprConsentPage = history?.location?.pathname === "/public/gdpr-consent";
  const [openStatement, setOpenStatement] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);

  const handleOpenStatement = useCallback(() => {
    setOpenStatement((prev) => !prev);
  }, []);

  useEffect(() => {
    setTimeout(() => setLoading(false), 5000);
  }, []);

  useEffect(() => {
    if (!token) return;

    const decoded = jwt.decode(token, { complete: true });

    if (!decoded || Date.now() >= decoded.payload.exp * 1000) {
      if (isGdprConsentPage) {
        setTokenExpired(true);
        (stateLens.tokenExpired |> set)(true);
        return;
      }
      return history.push({ pathname: `/` });
    }

    const dataFromToken = {
      quoteId: parseInt(decoded.payload.quote_id),
      transactionId: decoded.payload.transaction_id,
      userId: decoded.payload.user_id,
    };
    (stateLens.data |> set)(dataFromToken);
    setAccessToken(token);
  }, [token, isGdprConsentPage, setTokenExpired]);

  const { data: userData } = useQueryWithErrorHandling(PUBLIC_USER_DATA_QUERY, { skip: !(stateLens.userId |> get) });

  if (loading) return <PublicSecureLoading />;

  return (
    <Container className={cx(classes.root, classes.scrollArea)}>
      {openStatement ? (
        <PrivacyStatement onOpenStatement={handleOpenStatement} />
      ) : (
        <Container>
          <Container maxWidth="md" className={classes.firstCardPadding}>
            <PublicHeader
              stateLens={stateLens}
              tokenExpired={tokenExpired}
              email={userData?.publicUserData?.email}
              givenName={userData?.publicUserData?.firstName || emptyString}
              familyName={userData?.publicUserData?.lastName || emptyString}
              phoneNumber={userData?.publicUserData?.mobilePhone}
            />
          </Container>
          <Container maxWidth="md" className={classes.firstCardPadding}>
            <Component
              stateLens={stateLens}
              tokenExpired={tokenExpired}
              email={userData?.publicUserData?.email}
              givenName={userData?.publicUserData?.firstName || emptyString}
              familyName={userData?.publicUserData?.lastName || emptyString}
              phoneNumber={userData?.publicUserData?.mobilePhone}
            />
          </Container>
          <Container maxWidth="md" className={classes.publicFooterContainer}>
            <PublicFooter stateLens={stateLens} />
          </Container>
        </Container>
      )}
      <Container>
        <FooterMain openStatement={openStatement} onOpenStatement={handleOpenStatement} isPublic={isPublic} />
      </Container>
    </Container>
  );
};

export default PublicComponent;
